import { Paginated, Query } from "@feathersjs/feathers";
import { action, computed, makeObservable, observable, reaction, runInAction, when } from "mobx";
import { computedFn } from "mobx-utils";
import { BaseDataStore, RootStore } from ".";
import { Space } from "../models";
import { DataType, ISpace, ISpaceUser, LoadingStatus, Services, SpaceType, UserStatus } from "../types";
import { getRoutePath, Page, RoomView } from "src/routes";
import toast from "react-hot-toast";
import i18next from "src/i18n";
import client from "src/feathers";
import { add } from "date-fns";
import { Option } from "src/components";
import { sortAlph } from "src/util/util";


export interface SpaceNode {
  space:     Space,
  parent?:   Space,
  children?: SpaceNode[]
}


export class SpaceStore extends BaseDataStore<Space, ISpace> {
  public static readonly CookiePrefix: Record<SpaceType, string> = {
    [SpaceType.Workspace]: "op_workspace_id=",
    [SpaceType.Project]:   "op_project_id=",
    [SpaceType.Room]:      "op_room_id=",
    [SpaceType.System]:    "op_system=",
  };

  private space_link_service = client.service(Services.SpaceUsers);
  showSpaceSettingsModal: boolean = false;
  curDate: Date = new Date(); // selected date in the calendar. Used inside the room and on the dashboard
  projectFilter: Option<Space>[] = []; // Project filter on the dashboard 
  
  constructor(readonly rootStore: RootStore) {
    super(rootStore, Space.create, DataType.Space);

    makeObservable(this, {
      curDate:                observable,
      showSpaceSettingsModal: observable,
      projectFilter:          observable,

      changeRoom:               action,
      changeWorkspace:          action,
      changeProject:            action,
      setCurDate:               action,
      toggleSpaceSettingsModal: action,
      changeSpace:              action,
      changeProjectFilter:      action,

      getCurSpace:    computed,
      onDashboard:    computed,
      inRoom:         computed,
      getCurRoom:     computed,
      getUserRoomIDs: computed,
      getRoomForest:  computed,
    });

    // If the page loads up on a route that is in a room, change to that room
    // when(() => Boolean(this.rootStore.routingStore.pathName && this.rootStore.authStore.getCurUser), () => {
    //   if(this.rootStore.routingStore.pathName.startsWith("/room/")) {
    //     console.log('Page loads up on a route that is in a room, change to that room')
    //     this.changeRoom(Number(this.rootStore.routingStore.pathName.split("/")[2]));
    // }})
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  init() {
    super.init();

    // unset current room for user if room is deleted
    this.eventCallBacks.after.removed.push(async(spaceData: Space) => {
      if (spaceData.type === SpaceType.Room && Number(spaceData.id) === Number(this.rootStore.authStore.getCurRoomID)) {
        this.changeSpace();
      }
      if (spaceData.type === SpaceType.Project && Number(spaceData.id ) === Number( this.rootStore.authStore.getCurProjectID ) ) {
        runInAction(() => {
          this.rootStore.authStore.getCurUser.project_id = null;
          this.rootStore.authStore.getCurUser.room_id    = null;
        });

        this.rootStore.routingStore.push(getRoutePath(Page.WorkSpaceSettings, {id: this.rootStore.authStore.getCurWorkspaceID }));
        this.changeSpace();
      }
      if (spaceData.type === SpaceType.Workspace && Number(spaceData.id ) === Number( this.rootStore.authStore.getCurRoomID ) ) {
        runInAction(() => {
          this.rootStore.authStore.getCurUser.workspace_id = null;
          this.rootStore.authStore.getCurUser.project_id   = null;
          this.rootStore.authStore.getCurUser.room_id      = null;
        });

        this.rootStore.routingStore.push(getRoutePath(Page.Dashboard));
        this.changeSpace();
      }
    });

    // Enter room from link
    reaction(() => [this.rootStore.routingStore.pathName, this.rootStore.authStore.getCurUser], async () => {
      if (this.status !== LoadingStatus.Finished) return;

      if (this.rootStore.routingStore.pathName.startsWith("/room/")) {
        if (!this.rootStore.authStore.getCurUser) return;
        await this.changeRoom(Number(this.rootStore.routingStore.pathName.split("/")[2]));
      }
    }, { name: "routing_room_from_link" });

    // Load initial data on login:
    when(() => this.rootStore.authStore.isLoggedIn, () => {

      // Be sure this runs so we wont forget it
      if(this.rootStore.authStore.getCurWorkspaceID) {
        this.setSpaceCookie(SpaceType.Workspace);
      }

      this.loadItems(this.getCurFilter());
    }, { name: "space_login_load" });

    // Setup reactions
    reaction(() => this.rootStore.authStore.getCurWorkspaceID, () => {
      if (this.rootStore.authStore.getCurWorkspaceID) {
        this.loadItems(this.getCurFilter());
      }
    }, { name: "space_workspace_load" });
  }

  protected getCurFilter = (): Query => ({
  });

  /*******************************************************************************************************************************************
   * Getter 
   ********************************************************************************************************************************************/
  getUserSpaces = computedFn((type: SpaceType): Space[] => {
    return sortAlph(
      Object.keys(this.rootStore.authStore.getCurUser.spaces)
        .map(s => this.container[Number(s)])
        .filter(s => s && s.type === type)
    , s => s.title?.toLocaleLowerCase());
  });

  get getUserRoomIDs(): number[] {
    return Object.keys(this.rootStore.authStore.getCurUser?.spaces || {})
      .map(id => Number(id))
      .filter(id => this.container[id] && this.container[id].type === SpaceType.Room);
  }

  /**
   * @return all spaces of type @type
   */
  getItemsByType = computedFn((type: SpaceType): Space[] => {
    return sortAlph(
      Object.values(this.container).filter(s => s.type === type)
    , s => s.title?.toLocaleLowerCase());
  });

  /**
   * @return all spaces of type @type
   */
  getItemsByParent = computedFn((parent: number, type: SpaceType): Space[] => {
    return sortAlph(this.getItems.filter(s =>
      (s.parent === parent) && (!type || s.type === type) // Filter type
    ), s => s.title.toLocaleLowerCase());
  });
  /**
   * @return all spaces of type @type
   */
  getProjectRooms = computedFn((project: number): Space[] => {
    return sortAlph(
      this.getItems.filter(s =>
        (s.project_id === project) && (s.type === SpaceType.Room)
      )
    , s => s.title?.toLocaleLowerCase());
  });
  /**
   * @return all spaces of type @type
   */
  getUserItemsByParent = computedFn((parent: number, type: SpaceType): Space[] => {
    return sortAlph(
      this.getItems.filter(s =>
        (s.parent === parent || s.workspace_id === parent || s.project_id === parent) && // Filter parent relation
        (!type || s.type === type) && // Filter type
        (this.rootStore.authStore?.getCurUser?.spaces[s.id]) // Filter membership
      )
    , s => s.title?.toLocaleLowerCase());
  });


  get getCurSpace(): ISpace {
    if (this.inWorkspace) return this.getItemByID(this.rootStore.authStore.getCurWorkspaceID)
    if (this.inProject) return this.getItemByID(this.rootStore.authStore.getCurProjectID)
    if (this.inRoom) return this.getItemByID(this.rootStore.authStore.getCurRoomID)
    return this.getItemByID(this.rootStore.authStore.getCurWorkspaceID) || null;
  }


  get getCurRoom(): Space {
    return this.rootStore.authStore.getCurRoomID ? this.getItemByID(this.rootStore.authStore.getCurRoomID) : null;
  }

  get onDashboard(): boolean {
    return this.rootStore.routingStore.getPathName === "/" + Page.Home && !this.rootStore.authStore.getCurRoomID;
  }

  get inWorkspace(): boolean {
    return this.rootStore.routingStore.getPathName && this.rootStore.routingStore.getPathName.startsWith("/workspace");
  }

  get inProject(): boolean {
    return this.rootStore.routingStore.getPathName && this.rootStore.routingStore.getPathName.startsWith("/" + Page.ProjectSettings);
  }

  get inRoom(): boolean {
    return this.rootStore.routingStore.getPathName && this.rootStore.routingStore.getPathName.startsWith("/" + Page.Room);
  }

  goToDashboard = async () => {
    this.changeSpace(null);
    this.rootStore.routingStore.push(getRoutePath(Page.Home));
  }

  projectFilterHasProject = computedFn((project_id: number): boolean => {
    return this.projectFilter.some(o => Number(o.value.id) === Number(project_id));
  });

  /*******************************************************************************************************************************************
   * Setter 
   ********************************************************************************************************************************************/
  setCurDate = (date: Date) => {
    if (this.curDate.getTime() !== date.getTime()) this.curDate = date;
  }
  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/

  changeWorkspace = async (id: number) => {
    if (this.rootStore.authStore.getCurWorkspaceID === id) return;
    await this.goToDashboard();
    runInAction(() => {
      this.rootStore.authStore.getCurUser.project_id = null;
      this.rootStore.authStore.getCurUser.room_id    = null;
    });

    await this.rootStore.authStore.getCurUser.save({ workspace_id: id });
    this.changeSpace();
  }

  changeRoom = async (id: number, route?: boolean): Promise<boolean> => {

    if (this.rootStore.authStore.getCurRoomID === id) {
      if (route) this.rootStore.routingStore.push(getRoutePath(Page.Room, { id, view: RoomView.Deliverable }));
      return true;
    }

    if (this.status !== LoadingStatus.Finished) {
      when(() => this.status === LoadingStatus.Finished, () => this.changeRoom(id, route));
      return false;
    }

    if (!id || !this.rootStore.spaceStore.container[id]) {
      if (id) {
        // If the id is not in the container after the load has finished, the user
        // either does not have access to this room or it is in a different workspace
        const link_data: Paginated<ISpaceUser> = await this.space_link_service.find({
          query: {
            $select: ['workspace_id'],
            $limit: 1,
            user_id: this.rootStore.authStore.getCurUserID,
            space_id: id,
          }
        }) as unknown as Paginated<ISpaceUser>;
        if (link_data.total) {
          await this.changeWorkspace(link_data.data[0].workspace_id);
          return this.changeRoom(id, route);
        } else {
          toast.error(i18next.t("room_not_available"));
          this.rootStore.routingStore.push(getRoutePath(Page.Dashboard));
        }

      }
      return false;
    }

    if (route) {
      this.rootStore.routingStore.push(getRoutePath(Page.Room, { id, view: RoomView.Deliverable }));
    }
    return true;
  }

  changeProject = async (id: number, route?: boolean) => {
    this.rootStore.routingStore.push(getRoutePath(Page.ProjectSettings, { id, view: 'settings' }));
  }

  loadSpace = async (space_id: number) => {
    if (this.container[space_id] || !space_id) return;
    try {
      const space: ISpace = await this.service.get(space_id);
      runInAction(() => {
        this.container[space_id] = Space.create(space);
        this.container = Object.assign({}, this.container);
      });
    } catch (err) {
      console.error("Failed to load space", err);
    }
  }

  unLoadSpace = async (space_id: number) => {
    if (!this.container[space_id]) return;
    try {
      delete this.container[space_id];
      this.container = Object.assign({}, this.container);
    } catch (err) {
      console.error("Failed to load space", err);
    }
  }

  /**
   * @return forest structure of rooms and sub rooms.
   */
  get getRoomForest(): SpaceNode[] {
    const cur_project_id = this.rootStore.authStore.getCurProjectID;
    if (!cur_project_id) return [];

    const user_spaces: Space[] = this.getUserSpaces(SpaceType.Room);
    const project_subrooms: Space[] = user_spaces.filter(r =>
      r.project_id === cur_project_id 
      && r.isSubRoom 
      && user_spaces.find(s => s.id === r.parent)
    );

    const parent_rooms: Space[] = user_spaces.filter(r =>
      r.project_id === cur_project_id && 
      ( r.project_id === r.parent || !user_spaces.find(s => s.id === r.parent))
    );

    const getChildren = (parent: Space): SpaceNode[] => {
      const children: Space[] = project_subrooms.filter(r => r.parent === parent.id);
      return children?.map(c => ({ space: c, parent, children: getChildren(c) })) || [];
    }

    return sortAlph(
      parent_rooms.map(r => ({ space: r, children: sortAlph<SpaceNode>(getChildren(r), node => node?.space?.title?.toLocaleLowerCase()) }))
    , n => n.space.title.toLowerCase());
  }


  /**
   * @return array of spaces that contain the space object of @space_id 
   * and all its parents up to the workspace in order, e.g.
   * [workspace, project, room, sub room 1 , space_id room]
   */
  getSpacePath = (space_id: number): Space[] => {
    if (!this.container[space_id]) return [];
    try {
      let child = this.container[space_id];
      const tree = [child];
      while (child != null) {
        let parent = this.container[child.parent];
        if (parent) {
          tree.unshift(parent)
          child = parent;
        } else {
          child = null;
        }
      }
      return tree;
    } catch (err) {
      console.error("Failed to create space tree", err);
    }
  }

  toggleSpaceSettingsModal = (toggle?: boolean) => {
    this.showSpaceSettingsModal = toggle ? toggle : !this.showSpaceSettingsModal;
  };

  /**
   * Sets current room, project and workspace for the user to ensure it points to same during session but also after reload browser.
   * Will updates cookies with the current room/project/workspace information. 
   */
  changeSpace = async (space_id?: number) => {
    // Ignore routing when user is not logged in
    if (!this.rootStore.authStore.isLoggedIn) return;

    let space: Space;
    if (!space_id) {
      runInAction(() => {
        this.rootStore.authStore.getCurUser.project_id = null;
        this.rootStore.authStore.getCurUser.room_id    = null;
      });
      SpaceStore.removeSpaceCookie(SpaceType.Project);
      SpaceStore.removeSpaceCookie(SpaceType.Room);
    } else {
      // Load that space in case its not in the container yet. 
      await this.loadSpace(Number(space_id));
      space = this.container[Number(space_id)];
  
      if(!space) throw new Error(`Failed to load user space ${space_id}`);
      
      // Update the cookies as well as the current user object 
      switch(space.type) {
        case SpaceType.Workspace:
          runInAction(() => {
            this.rootStore.authStore.getCurUser.project_id = null;
            this.rootStore.authStore.getCurUser.room_id    = null;
          });
          SpaceStore.removeSpaceCookie(SpaceType.Project);
          SpaceStore.removeSpaceCookie(SpaceType.Room);
          break;
        case SpaceType.Project:
          if(this.rootStore.authStore.getCurProjectID === space_id) return;
          runInAction(() => {
            this.rootStore.authStore.getCurUser.project_id = space.id;
            this.rootStore.authStore.getCurUser.room_id    = null;
          });
          this.setSpaceCookie(SpaceType.Project);
          SpaceStore.removeSpaceCookie(SpaceType.Room);
          break;
        case SpaceType.Room:
          if(this.rootStore.authStore.getCurRoomID === space_id) return;
          runInAction(() => {
            this.rootStore.authStore.getCurUser.project_id = space.project_id;
            this.rootStore.authStore.getCurUser.room_id    = space.id;
          });
          this.setSpaceCookie(SpaceType.Project);
          this.setSpaceCookie(SpaceType.Room);
          break;
        default: return;
      }
      this.setSpaceCookie(SpaceType.Workspace);
    }
    
    // Inform the server about the space change
    await client.service(Services.Session).changeSpace({
      workspace_id: this.rootStore.authStore.getCurWorkspaceID,
      project_id:   this.rootStore.authStore.getCurProjectID,
      room_id:      this.rootStore.authStore.getCurRoomID,
    });
    
    // Set user to accepted room invite if access the room and status still not Accepted
    if(  this.rootStore.authStore.getCurUser.getStatus(this.rootStore.authStore.getCurRoomID) != UserStatus.Accepted  && this.rootStore.authStore.getCurRoomID) {
      this.rootStore.authStore.getCurUser.getStatus(this.rootStore.authStore.getCurRoomID)
      this.rootStore.authStore.getCurUser.setSpaceSetting(
        this.rootStore.authStore.getCurRoomID,
        'status',
        UserStatus.Accepted,
        true
      );
    }

  }

  /**
   * Set cookie for @space_type to the ID of the current space of that type. Cookie is set to expire in one year. 
   */
  setSpaceCookie = (space_type: SpaceType) => {
    // Expiry date used in cookies is set to one year from now
    const expiry_date: Date   = add(new Date(), {years: 1});
    let space_id: string = "";
    switch (space_type) {
      case SpaceType.Room: 
        space_id = ""+this.rootStore.authStore.getCurRoomID;
        break;
      case SpaceType.Workspace: 
        space_id = ""+this.rootStore.authStore.getCurWorkspaceID;
        break;
      case SpaceType.Project:
        space_id = ""+this.rootStore.authStore.getCurProjectID;
        break;
      case SpaceType.System:
      default: break;
    }
    document.cookie = SpaceStore.CookiePrefix[space_type] + space_id.toString() + "; expires=" + expiry_date.toUTCString() + "; path=/;";
  }

  /**
   * Unsets the cookie that remembers the current id for space of type @space_type 
   **/
  static removeSpaceCookie = (space_type: SpaceType) => {
    document.cookie = SpaceStore.CookiePrefix[space_type] + "; expires=" + new Date().toUTCString() + "; path=/;";
  }

  static clearSpaceCookies = () => {
    // Remove the cookies
    this.removeSpaceCookie(SpaceType.Room);
    this.removeSpaceCookie(SpaceType.Project);
    this.removeSpaceCookie(SpaceType.Workspace);
  };

  changeProjectFilter = (project: Option<Space>, add: boolean): void => {
    if(add) this.projectFilter.push(project);
    else this.projectFilter = this.projectFilter.filter(o => Number(o.value.id) !== Number(project.value.id));
  }
}

